import * as React from 'react'
import { useCallback, useState } from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { F0Icon } from '@factorialco/f0-react'
import { Coffee, Moon, Office, Salad } from '@factorialco/f0-react/icons/app'
import moment from 'moment'

import { useEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import FormBlock from 'modules/expenses/components/expenses/Detail/_newForm/Sections/FormBlock'
import { Expensable } from 'modules/expenses/types/Expensable'

interface Props {
  expensable?: Expensable
  onConceptsChange?: (concepts: LocalConcept[]) => void
  disabled?: boolean
}

type ConceptField =
  | 'breakfastProvided'
  | 'lunchProvided'
  | 'dinnerProvided'
  | 'accommodationProvided'

export interface LocalConcept {
  id: number
  date: string
  breakfastProvided: boolean
  lunchProvided: boolean
  dinnerProvided: boolean
  accommodationProvided: boolean
}

interface ConceptIconProps {
  isProvided: boolean
  icon: typeof Coffee
  ariaLabel: string
  onClick?: () => void
  disabled?: boolean
}

const ConceptIcon = ({
  isProvided,
  icon: IconComponent,
  ariaLabel,
  onClick,
  disabled = false,
}: ConceptIconProps) => (
  <div
    role="button"
    tabIndex={disabled ? -1 : 0}
    aria-label={ariaLabel}
    onClick={disabled ? undefined : onClick}
    onKeyDown={(e) => !disabled && e.key === 'Enter' && onClick?.()}
    style={{
      flex: '1 1 0',
      minWidth: 0,
      cursor: disabled ? 'default' : 'pointer',
      opacity: disabled && !isProvided ? 0.5 : 1,
    }}
  >
    <Box
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      padding="s8"
      height="s48"
      borderRadius={{ all: 'abs008' }}
      background={isProvided ? 'viridian100' : 'grey100'}
      border={{
        all: {
          width: 's1',
          style: 'solid',
          color: isProvided ? 'viridian600' : disabled ? 'grey200' : 'grey300',
        },
      }}
    >
      <F0Icon icon={IconComponent} size="md" color={isProvided ? 'default' : 'secondary'} />
    </Box>
  </div>
)

interface DayRowProps {
  concept: LocalConcept
  dayNumber: number
  onToggle: (conceptId: number, field: ConceptField) => void
  isLastDay: boolean
  disabled?: boolean
}

const getCompanyPaidLabel = (concept: LocalConcept): string => {
  const parts: string[] = []
  if (concept.breakfastProvided) {
    parts.push(i18n.t('expenses_v2.forms.germany_per_diem_modal.company_paid.breakfast'))
  }
  if (concept.lunchProvided) {
    parts.push(i18n.t('expenses_v2.forms.germany_per_diem_modal.company_paid.lunch'))
  }
  if (concept.dinnerProvided) {
    parts.push(i18n.t('expenses_v2.forms.germany_per_diem_modal.company_paid.dinner'))
  }
  if (concept.accommodationProvided) {
    parts.push(i18n.t('expenses_v2.forms.germany_per_diem_modal.company_paid.overnight'))
  }
  return parts.join(', ')
}

const DayRow = ({ concept, dayNumber, onToggle, isLastDay, disabled = false }: DayRowProps) => {
  const formattedDate = moment(concept.date).format('ddd, MMM D')
  const companyPaidLabel = getCompanyPaidLabel(concept)

  return (
    <Box flexDirection="column" gap="s8">
      <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center">
        <Text.Normal size="200" weight="medium" color="grey1000">
          {formattedDate}
        </Text.Normal>
        <Text.Normal size="200" color="grey600">
          {i18n.t('expenses_v2.forms.germany_per_diem_modal.company_paid.day_label', {
            day: dayNumber,
          })}
        </Text.Normal>
      </Box>
      <div style={{ display: 'flex', gap: '8px' }}>
        <ConceptIcon
          isProvided={concept.breakfastProvided}
          icon={Coffee}
          ariaLabel={i18n.t('expenses_v2.forms.germany_per_diem_modal.company_paid.breakfast')}
          onClick={() => onToggle(concept.id, 'breakfastProvided')}
          disabled={disabled}
        />
        <ConceptIcon
          isProvided={concept.lunchProvided}
          icon={Salad}
          ariaLabel={i18n.t('expenses_v2.forms.germany_per_diem_modal.company_paid.lunch')}
          onClick={() => onToggle(concept.id, 'lunchProvided')}
          disabled={disabled}
        />
        <ConceptIcon
          isProvided={concept.dinnerProvided}
          icon={Moon}
          ariaLabel={i18n.t('expenses_v2.forms.germany_per_diem_modal.company_paid.dinner')}
          onClick={() => onToggle(concept.id, 'dinnerProvided')}
          disabled={disabled}
        />
        <ConceptIcon
          isProvided={concept.accommodationProvided}
          icon={Office}
          ariaLabel={i18n.t('expenses_v2.forms.germany_per_diem_modal.company_paid.overnight')}
          onClick={() => onToggle(concept.id, 'accommodationProvided')}
          disabled={disabled || isLastDay}
        />
      </div>
      {companyPaidLabel && (
        <Text.Normal size="200" color="grey700">
          {`${i18n.t('expenses_v2.forms.germany_per_diem_modal.company_paid.deduction_label')}: ${companyPaidLabel}`}
        </Text.Normal>
      )}
    </Box>
  )
}

const getDaysBetween = (startDate: Date, endDate: Date): Date[] => {
  const result: Date[] = []
  const current = new Date(startDate)
  current.setUTCHours(0, 0, 0, 0)
  const end = new Date(endDate)
  end.setUTCHours(0, 0, 0, 0)
  while (current <= end) {
    result.push(new Date(current))
    current.setUTCDate(current.getUTCDate() + 1)
  }
  return result
}

/** Build one concept per day when server has no concept records (e.g. none selected on create). */
const buildConceptsFromDateRange = (startDate: string, endDate: string): LocalConcept[] => {
  const start = new Date(startDate)
  const end = new Date(endDate)
  const days = getDaysBetween(start, end)
  return days.map((date, index) => ({
    id: -(index + 1),
    date: date.toISOString().slice(0, 10),
    breakfastProvided: false,
    lunchProvided: false,
    dinnerProvided: false,
    accommodationProvided: false,
  }))
}

export const mapServerConceptsToLocal = (
  serverConcepts: Array<{
    id: number
    date: string
    breakfastProvided?: boolean | undefined
    lunchProvided?: boolean | undefined
    dinnerProvided?: boolean | undefined
    accommodationProvided?: boolean | undefined
  }>
): LocalConcept[] => {
  const mapped = serverConcepts
    .map((c) => ({
      id: c.id,
      date: c.date,
      breakfastProvided: Boolean(c.breakfastProvided),
      lunchProvided: Boolean(c.lunchProvided),
      dinnerProvided: Boolean(c.dinnerProvided),
      accommodationProvided: Boolean(c.accommodationProvided),
    }))
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())

  // Last day cannot have overnight stay
  const lastConcept = mapped.at(-1)
  if (lastConcept) {
    lastConcept.accommodationProvided = false
  }

  return mapped
}

const DailyConcepts = (props: Props) => {
  const { expensable, onConceptsChange, disabled = false } = props
  const perDiem = expensable?.perDiem
  const serverConcepts = perDiem?.perDiemConcepts || []

  const [localConcepts, setLocalConcepts] = useState<LocalConcept[]>([])

  const conceptsKey = serverConcepts
    .map((c) => c.id)
    .sort()
    .join(',')

  const startDate = perDiem?.startDate ?? null
  const endDate = perDiem?.endDate ?? null

  // Sync local state: from server concepts when present, else from trip date range when no concepts were saved
  useEffect(() => {
    if (serverConcepts.length > 0) {
      setLocalConcepts(mapServerConceptsToLocal(serverConcepts))
    } else if (startDate && endDate) {
      setLocalConcepts(buildConceptsFromDateRange(startDate, endDate))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conceptsKey, expensable?.id, startDate, endDate])

  const handleToggle = useCallback(
    (conceptId: number, field: ConceptField) => {
      setLocalConcepts((prev) => {
        const updated = prev.map((concept) =>
          concept.id === conceptId ? { ...concept, [field]: !concept[field] } : concept
        )
        // Defer so parent setState from onConceptsChange does not run during this update
        queueMicrotask(() => onConceptsChange?.(updated))
        return updated
      })
    },
    [onConceptsChange]
  )

  const isAllSelected = useCallback(
    (field: ConceptField): boolean => {
      if (localConcepts.length === 0) return false
      const conceptsToCheck =
        field === 'accommodationProvided' ? localConcepts.slice(0, -1) : localConcepts
      return conceptsToCheck.every((c) => c[field])
    },
    [localConcepts]
  )

  const handleToggleAll = useCallback(
    (field: ConceptField) => {
      const allSelected = isAllSelected(field)
      setLocalConcepts((prev) => {
        const updated = prev.map((concept, index) => {
          const isLastDay = index === prev.length - 1
          const value = field === 'accommodationProvided' && isLastDay ? false : !allSelected
          return { ...concept, [field]: value }
        })
        // Defer so parent setState from onConceptsChange does not run during this update
        queueMicrotask(() => onConceptsChange?.(updated))
        return updated
      })
    },
    [isAllSelected, onConceptsChange]
  )

  if (localConcepts.length === 0) {
    return null
  }

  return (
    <FormBlock
      title={i18n.t('expenses_v2.forms.germany_per_diem_modal.company_paid.title')}
      subtitle={i18n.t('expenses_v2.forms.germany_per_diem_modal.company_paid.description')}
    >
      <Box flexDirection="column" gap="s16">
        <Box flexDirection="row" justifyContent="spaceBetween">
          <Button
            type={isAllSelected('breakfastProvided') ? 'primary' : 'secondary'}
            onClick={() => handleToggleAll('breakfastProvided')}
            disabled={disabled}
          >
            {i18n.t('expenses_v2.forms.germany_per_diem_modal.company_paid.all_breakfasts')}
          </Button>
          <Button
            type={isAllSelected('lunchProvided') ? 'primary' : 'secondary'}
            onClick={() => handleToggleAll('lunchProvided')}
            disabled={disabled}
          >
            {i18n.t('expenses_v2.forms.germany_per_diem_modal.company_paid.all_lunches')}
          </Button>
          <Button
            type={isAllSelected('dinnerProvided') ? 'primary' : 'secondary'}
            onClick={() => handleToggleAll('dinnerProvided')}
            disabled={disabled}
          >
            {i18n.t('expenses_v2.forms.germany_per_diem_modal.company_paid.all_dinners')}
          </Button>
          <Button
            type={isAllSelected('accommodationProvided') ? 'primary' : 'secondary'}
            onClick={() => handleToggleAll('accommodationProvided')}
            disabled={disabled}
          >
            {i18n.t('expenses_v2.forms.germany_per_diem_modal.company_paid.all_overnights')}
          </Button>
        </Box>
        {localConcepts.map((concept, index) => (
          <DayRow
            key={concept.id}
            concept={concept}
            dayNumber={index + 1}
            onToggle={handleToggle}
            isLastDay={index === localConcepts.length - 1}
            disabled={disabled}
          />
        ))}
      </Box>
    </FormBlock>
  )
}

export default DailyConcepts
